import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ColorPainter() {
  const [selectedColor, setSelectedColor] = useState('dark-brown');

  const imageMap = {
    'ral-custom': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/21fde4676_9022ce77-2f31-4dcc-954e-3a62ba9979c8.jpg',
    'red': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/a9509d477_Rdfarge.jpg',
    'light-grey': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/1f69daeef_Gra.jpg',
    'kebony': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/067e20ff3_332776da-c517-4eee-8edc-fdf991b911c8.jpg',
    'kebony-grey': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/5e387e694_5ac2beb9-593b-4411-ae97-962e4c86b904.jpg',
    'dark-brown': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/dc475fd11_7b46b8b8-8353-48bf-aaaa-2130501a29c8.jpg',
    'black': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/3b7edf984_a0efa3e3-844a-4529-92f7-54e2f71ccd01.jpg',
    'thermo-oil': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/fea49f211_127c3a2b-1e81-422c-9e5c-522c31788b11.jpg',
    'default': 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/942b28aee_LYKKEUMI.jpg'
  };

  const colors = [
    { id: 'ral-custom', name: 'RAL Farge', hex: '#A0A0A0', hueShift: 0 },
    { id: 'dark-brown', name: 'Mørk Brunn', hex: '#6B4423', hueShift: 15, default: true },
    { id: 'black', name: 'Sort', hex: '#1A1A1A', hueShift: 0 },
    { id: 'light-grey', name: 'Lys Grå', hex: '#C8C8C8', hueShift: 0 },
    { id: 'thermo-oil', name: 'Termofuru Olje', hex: '#D4A574', hueShift: 25 },
    { id: 'kebony', name: 'Kebony Radiata', hex: '#8B7355', hueShift: 20 },
    { id: 'kebony-grey', name: 'Kebony Patina', hex: '#A9A9A9', hueShift: 5 },
    { id: 'red', name: 'Rød', hex: '#8B3A3A', hueShift: 0 }
  ];

  const selectedColorObj = colors.find(c => c.id === selectedColor);

  return (
    <div className="grid md:grid-cols-2 gap-12 items-center mb-32">
      {/* Preview Image */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-[12px] overflow-hidden"
      >
        <div className="relative">
          <img
            src={imageMap[selectedColor] || imageMap.default}
            alt="Badstue"
            className="w-full h-[250px] md:h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
          <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-sm rounded-lg px-4 py-3">
            <p className="text-white font-heading text-sm tracking-widest">{selectedColorObj?.name}</p>
            <p className="text-white/70 font-body text-xs">{selectedColorObj?.hex}</p>
          </div>
        </div>
      </motion.div>

      {/* Color Picker */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
      >
        <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Velg Farge</p>
        <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Se Badstuen i <span className="italic">Din Farge</span></h3>
        
        <div className="grid grid-cols-2 gap-4">
          {colors.map((color) => (
            <motion.button
              key={color.id}
              onClick={() => setSelectedColor(color.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`relative rounded-[8px] p-4 transition-all duration-300 ${
                selectedColor === color.id
                  ? 'ring-2 ring-[#2d5a3d] bg-secondary/30 border border-[#2d5a3d]'
                  : 'bg-white border-2 border-border/20 hover:border-[#2d5a3d]/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg border-2 border-white shadow-md flex-shrink-0"
                  style={{ backgroundColor: color.hex }}
                />
                <div className="text-left">
                   <p className="font-heading text-2xl font-light text-[#2a1810]">{color.name}</p>
                   <p className="font-body text-2xl text-foreground/50">{color.hex}</p>
                 </div>
              </div>
              
              {selectedColor === color.id && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2 w-4 h-4 bg-[#2d5a3d] rounded-full"
                />
              )}
            </motion.button>
          ))}
        </div>

        <p className="font-body text-2xl text-foreground/60 mt-8 leading-relaxed">
           Klikk på en fargeprøve for å se hvordan den vil se ut på din badstue. Hvert alternativ viser en realistisk fremstilling av fargen under naturlig lys.
         </p>
      </motion.div>
    </div>
  );
}